import React, { useState, useRef, useEffect } from "react";
import "./DatePicker.css";
import CustomError from "../Errors/CustomError";
import Asterisks from "../Asterisks/Asterisks";

const DateRangePicker = ({
  label,
  value = [],
  onChange,
  minDate = "",
  maxDate = "",
  parentClassName = "",
  inputClassName = "",
  labelClassName = "",
  placeholder = "Select date range",
  parentStyle = {},
  inputStyle = {},
  labelStyle = {},
  readOnly = false,
  disabled = false,
  required = false,
  autocomplete = "off",
  customError,
  asterisk,
}) => {
  const [startDate, endDate] = value || [];
  const [showCalendar, setShowCalendar] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [hoverDate, setHoverDate] = useState(null);
  const calendarRef = useRef(null);

  // Close calendar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputClick = () => {
    if (!readOnly && !disabled) {
      setShowCalendar(!showCalendar);
    }
  };

  const handleDateSelect = (date) => {
    if (!startDate || (startDate && endDate)) {
      // If no start date or both dates are selected, start new selection
      onChange([date, null]);
    } else if (date < startDate) {
      // If selected date is before start date, make it the new start date
      onChange([date, null]);
    } else {
      // Otherwise set as end date
      onChange([startDate, date]);
      setShowCalendar(false);
    }
  };

  const handleHoverDate = (date) => {
    if (startDate && !endDate) {
      setHoverDate(date);
    }
  };

  const handleMonthChange = (increment) => {
    let newMonth = currentMonth + increment;
    let newYear = currentYear;

    if (newMonth < 0) {
      newMonth = 11;
      newYear--;
    } else if (newMonth > 11) {
      newMonth = 0;
      newYear++;
    }

    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
  };

  const clearDates = () => {
    onChange([null, null]);
    setShowCalendar(false);
  };

  const isDateInRange = (date) => {
    if (!startDate) return false;
    if (startDate && !endDate && hoverDate) {
      return (
        (date >= startDate && date <= hoverDate) ||
        (date >= hoverDate && date <= startDate)
      );
    }
    if (startDate && endDate) {
      return date >= startDate && date <= endDate;
    }
    return false;
  };

  const isStartDate = (date) => {
    return startDate && date.toDateString() === startDate.toDateString();
  };

  const isEndDate = (date) => {
    return endDate && date.toDateString() === endDate.toDateString();
  };

  const renderCalendar = () => {
    const firstDay = new Date(currentYear, currentMonth, 1).getDay();
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    const daysInPrevMonth = new Date(currentYear, currentMonth, 0).getDate();

    const days = [];
    // Previous month days
    for (let i = firstDay - 1; i >= 0; i--) {
      const day = daysInPrevMonth - i;
      const date = new Date(currentYear, currentMonth - 1, day);
      days.push(
        <td
          key={`prev-${day}`}
          className="calendar-day other-month"
          onClick={() => handleMonthChange(-1)}
        >
          {day}
        </td>
      );
    }

    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentYear, currentMonth, day);
      const isSelectedStart = isStartDate(date);
      const isSelectedEnd = isEndDate(date);
      const isInRange = isDateInRange(date);
      const isToday = date.toDateString() === new Date().toDateString();

      let className = "calendar-day";
      if (isSelectedStart) className += " selected-start";
      if (isSelectedEnd) className += " selected-end";
      if (isInRange) className += " in-range";
      if (isToday) className += " today";
      if (date < new Date(minDate) || date > new Date(maxDate)) {
        className += " disabled";
      }

      days.push(
        <td
          key={`current-${day}`}
          className={className}
          onClick={() => handleDateSelect(date)}
          onMouseEnter={() => handleHoverDate(date)}
        >
          {day}
        </td>
      );
    }

    // Next month days
    const daysToAdd = 42 - days.length; // 6 rows x 7 days
    for (let day = 1; day <= daysToAdd; day++) {
      const date = new Date(currentYear, currentMonth + 1, day);
      days.push(
        <td
          key={`next-${day}`}
          className="calendar-day other-month"
          onClick={() => handleMonthChange(1)}
        >
          {day}
        </td>
      );
    }

    // Split days into weeks
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(<tr key={`week-${i}`}>{days.slice(i, i + 7)}</tr>);
    }

    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    return (
      <div className="calendar-container" ref={calendarRef}>
        <div className="calendar-header">
          <button
            type="button"
            className="calendar-nav-button"
            onClick={() => handleMonthChange(-1)}
          >
            &lt;
          </button>
          <div className="calendar-month-year">
            {monthNames[currentMonth]} {currentYear}
          </div>
          <button
            type="button"
            className="calendar-nav-button"
            onClick={() => handleMonthChange(1)}
          >
            &gt;
          </button>
        </div>
        <table className="calendar-table">
          <thead>
            <tr>
              <th>Sun</th>
              <th>Mon</th>
              <th>Tue</th>
              <th>Wed</th>
              <th>Thu</th>
              <th>Fri</th>
              <th>Sat</th>
            </tr>
          </thead>
          <tbody>{weeks}</tbody>
        </table>
        <div className="calendar-footer">
          <button
            type="button"
            className="calendar-clear-button"
            onClick={clearDates}
          >
            Clear
          </button>
          <button
            type="button"
            className="calendar-today-button"
            onClick={() => {
              const today = new Date();
              handleDateSelect(today);
            }}
          >
            Today
          </button>
        </div>
      </div>
    );
  };

  const formatDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
  };

  const displayValue = startDate
    ? `${formatDate(startDate)} - ${endDate ? formatDate(endDate) : ""}`
    : "";

  return (
    <div
      className={
        parentClassName && parentClassName.trim() !== ""
          ? parentClassName
          : "default-date-piker-parent-class"
      }
      style={parentStyle}
    >
      <label
        className={
          labelClassName && labelClassName.trim() !== ""
            ? labelClassName
            : "default-date-piker-label-class"
        }
        style={labelStyle}
      >
        {label} <Asterisks asterisk={asterisk} />
      </label>
      <div className="date-range-picker-container">
        <div className="date-range-input-wrapper">
          <input
            type="text"
            value={displayValue}
            readOnly
            onClick={handleInputClick}
            placeholder={placeholder}
            className={`${
              inputClassName && inputClassName.trim() !== ""
                ? inputClassName
                : "default-date-piker-input-class"
            } ${customError ? "date-piker-input-error" : ""}`}
            style={{ ...inputStyle, textTransform: "uppercase" }}
            disabled={disabled}
            required={required}
            autoComplete={autocomplete}
          />
          {showCalendar && renderCalendar()}
        </div>
        {customError && <CustomError error={customError} />}
      </div>
    </div>
  );
};

export default DateRangePicker;
